import { safeJson } from "src/app/helpers/Index";
import { faCartPlus, faTrash } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Toast } from "primereact/toast";
import React from "react";
import { AddOnService } from "src/app/services/Index";
import "./AdminExtras.css";

type Props = {};
type State = {
	available: boolean;
	isLoading: boolean;
	saving: boolean;
	addOns: any[];
	showDialog: boolean;
	edit: any;
};

const money = (pence: any) => `£${((Number(pence) || 0) / 100).toFixed(2)}`;

/**
 * Add-ons — the things sold alongside a ticket.
 *
 * Worth a club's attention out of proportion to the prices on screen: the
 * margin on a £4 programme is a lot better than the margin on a £15 ticket.
 */
export class AdminAddOns extends React.Component<Props, State> {
	toast: any;

	constructor(props: Props) {
		super(props);
		this.state = { available: true, isLoading: true, saving: false, addOns: [], showDialog: false, edit: null };
	}

	componentDidMount() {
		this.load();
	}

	async load() {
		try {
			const status = await safeJson(AddOnService.status());
			if (status?.available === false) {
				this.setState({ available: false, isLoading: false });
				return;
			}
			const addOns = await safeJson(AddOnService.getAll());
			this.setState({ addOns: Array.isArray(addOns) ? addOns : [], isLoading: false });
		} catch {
			this.setState({ available: false, isLoading: false });
		}
	}

	open(addOn?: any) {
		this.setState({
			edit: addOn
				? { ...addOn, pricePounds: (Number(addOn.pricePence) || 0) / 100 }
				: { isActive: true, name: "", pricePounds: 0, sortOrder: this.state.addOns.length },
			showDialog: true
		});
	}

	async save() {
		const a = this.state.edit;
		if (!a?.name?.trim()) return;

		this.setState({ saving: true });
		const payload = {
			capacity: a.capacity || null,
			description: a.description || null,
			isActive: !!a.isActive,
			maxPerOrder: a.maxPerOrder || null,
			name: a.name.trim(),
			pricePence: Math.round((Number(a.pricePounds) || 0) * 100),
			sortOrder: Number(a.sortOrder) || 0
		};

		const res = a.id ? await AddOnService.update(a.id, payload) : await AddOnService.add(payload);
		this.setState({ saving: false });

		if (!res.ok) {
			const message = await res.text();
			this.toast.show({ closable: false, detail: message || "Please try again later.", life: 5000, severity: "error", summary: "Couldn't save" });
			return;
		}
		this.setState({ edit: null, showDialog: false });
		this.load();
	}

	async remove(addOn: any) {
		if (!window.confirm(`Remove "${addOn.name}"? Orders that included it keep working.`)) return;
		await AddOnService.remove(addOn.id);
		this.load();
	}

	render() {
		const { available, isLoading, addOns } = this.state;
		const edit = this.state.edit;

		if (!isLoading && !available) {
			return (
				<div style={{ padding: 20 }}>
					<h2 style={{ marginBottom: 20 }}>Add-ons</h2>
					<div className="ex-empty">
						<FontAwesomeIcon icon={faCartPlus} />
						<h3>Add-ons aren&rsquo;t switched on yet</h3>
						<p>The add-on tables haven&rsquo;t been created on this environment.</p>
					</div>
				</div>
			);
		}

		return (
			<div style={{ padding: 20 }}>
				<Toast ref={(el) => (this.toast = el)} />
				<h2 style={{ marginBottom: 6 }}>Add-ons</h2>
				<p style={{ color: "var(--pb-text-muted)", marginBottom: 20 }}>
					Things sold alongside a ticket &mdash; parking, a programme, a pie and a pint.
				</p>

				<div style={{ marginBottom: 18 }}>
					<Button icon="pi pi-plus" label="Add an add-on" onClick={() => this.open()} />
				</div>

				{isLoading && <p style={{ color: "var(--pb-text-muted)" }}>Loading&hellip;</p>}

				{!isLoading && addOns.length === 0 &&
					<div className="ex-empty">
						<FontAwesomeIcon icon={faCartPlus} />
						<h3>No add-ons yet</h3>
						<p>The margin on a programme beats the margin on a ticket. Add one and it&rsquo;s offered at checkout.</p>
					</div>}

				<div className="ex-grid">
					{addOns.map((a) => (
						<div className={`ex-tile${a.isActive ? "" : " off"}`} key={`a-${a.id}`}>
							<div className="ex-tile-head">
								<FontAwesomeIcon icon={faCartPlus} />
								{!a.isActive && <span className="ex-badge">OFF</span>}
							</div>
							<h4>{a.name}</h4>
							<span className="ex-price">{money(a.pricePence)}</span>
							{a.description && <span className="ex-meta">{a.description}</span>}
							<span className="ex-meta">
								{a.sold} sold{a.remaining != null ? ` · ${a.remaining} left` : ""}
							</span>
							<span className="ex-meta">
								{a.linkedProducts === 0 ? "Offered with everything" : `Offered with ${a.linkedProducts} product${a.linkedProducts === 1 ? "" : "s"}`}
							</span>
							<div className="ex-actions">
								<Button className="p-button-text" label="Edit" onClick={() => this.open(a)} style={{ padding: "4px 8px" }} />
								<Button className="p-button-text p-button-danger" icon={<FontAwesomeIcon icon={faTrash} />} onClick={() => this.remove(a)} style={{ padding: "4px 8px" }} />
							</div>
						</div>
					))}
				</div>

				<Dialog footer={<>
					<Button className="p-button-text" label="Cancel" onClick={() => this.setState({ showDialog: false })} />
					<Button disabled={this.state.saving} icon="pi pi-check" label={this.state.saving ? "Saving…" : "Save"} onClick={() => this.save()} />
				</>} header={edit?.id ? "Edit add-on" : "New add-on"} modal onHide={() => this.setState({ showDialog: false })} style={{ width: 460 }} visible={this.state.showDialog}>
					{edit && <>
						<label className="ex-label">Name</label>
						<InputText autoFocus onChange={(e) => this.setState({ edit: { ...edit, name: e.target.value } })} placeholder="Matchday programme" style={{ marginBottom: 14, width: "100%" }} value={edit.name || ""} />

						<label className="ex-label">Description</label>
						<InputTextarea onChange={(e) => this.setState({ edit: { ...edit, description: e.target.value } })} rows={2} style={{ marginBottom: 14, width: "100%" }} value={edit.description || ""} />

						<label className="ex-label">Price</label>
						<InputNumber currency="GBP" locale="en-GB" mode="currency" onValueChange={(e) => this.setState({ edit: { ...edit, pricePounds: e.value } })} style={{ marginBottom: 14, width: "100%" }} value={edit.pricePounds} />

						<div className="ex-two">
							<div>
								<label className="ex-label">How many exist</label>
								<InputNumber onValueChange={(e) => this.setState({ edit: { ...edit, capacity: e.value } })} placeholder="Unlimited" style={{ width: "100%" }} value={edit.capacity} />
							</div>
							<div>
								<label className="ex-label">Max per order</label>
								<InputNumber onValueChange={(e) => this.setState({ edit: { ...edit, maxPerOrder: e.value } })} placeholder="No limit" style={{ width: "100%" }} value={edit.maxPerOrder} />
							</div>
						</div>
						<p className="ex-hint">A programme doesn&rsquo;t run out the way a parking space does &mdash; leave capacity blank for those.</p>

						<div className="ex-switch" style={{ marginTop: 16 }}>
							<InputSwitch checked={!!edit.isActive} onChange={(e) => this.setState({ edit: { ...edit, isActive: e.value } })} />
							<label>Offer it at checkout</label>
						</div>
					</>}
				</Dialog>
			</div>
		);
	}
}
